import React, { useEffect, useMemo, useState } from "react";
import StatCard from "./components/StatCard";
import TimeSeriesChart from "./components/TimeSeriesChart";
import TopKTable from "./components/TopKTable";
import { promQuery, promQueryRange, rangeWindowMs, toSeries } from "./prom";
import { apiJson } from "@/lib/api";
import { getTenantUuid } from "@/app/ingress/tenant";

function ageSec(ts: number | null) {
  if (!ts) return null;
  return Math.max(0, Math.floor(Date.now() / 1000 - ts));
}

export default function AdminObservabilityPlatformHealthPage() {
  const tenantUuid = getTenantUuid();
  const [apiOk, setApiOk] = useState<boolean | null>(null);
  const [dbOk, setDbOk] = useState<boolean | null>(null);

  const [queueDepthSeries, setQueueDepthSeries] = useState<any[]>([]);
  const [workerUp, setWorkerUp] = useState<number | null>(null);
  const [workerHeartbeat, setWorkerHeartbeat] = useState<number | null>(null);

  const [topSlugs, setTopSlugs] = useState<Array<Record<string, any>>>([]);

  useEffect(() => {
    let alive = true;

    async function run() {
      // API health
      try {
        const h = await apiJson<any>("/v1/healthz");
        if (!alive) return;
        setApiOk(!!h?.ok);
      } catch {
        if (!alive) return;
        setApiOk(false);
      }

      // DB health (admin-only endpoint)
      try {
        const h = await apiJson<any>(`/v1/admin/observability/prom/platform_health?tenant_id=${tenantUuid}`);
        if (!alive) return;
        setDbOk(!!h?.db_ok);
      } catch {
        if (!alive) return;
        setDbOk(false);
      }

      // Worker up + heartbeat age (instant query)
      try {
        const up = await promQuery("max(ingress_worker_up)", tenantUuid);
        const upSeries = toSeries(up);
        const v = upSeries[0]?.points?.[0]?.v ?? null;
        if (!alive) return;
        setWorkerUp(v);
      } catch {}

      try {
        const hb = await promQuery("max(ingress_worker_heartbeat_unix)", tenantUuid);
        const hbSeries = toSeries(hb);
        const v = hbSeries[0]?.points?.[0]?.v ?? null;
        if (!alive) return;
        setWorkerHeartbeat(v);
      } catch {}

      // Queue depth (range)
      try {
        const { start, end } = rangeWindowMs(1); // last 1h
        const resp = await promQueryRange(`ingress_queue_depth_by_tenant{tenant_id="${tenantUuid}"}`, tenantUuid, { start, end, step: "15s" });
        //await promQueryRange("ingress_queue_depth", tenantUuid, { start, end, step: "15s" });
        const ss = toSeries(resp);

        // Stable colors by status for easy visual parsing
        const statusColor: Record<string, string> = {
          queued: "#2563eb",      // blue-600
          processing: "#f59e0b",  // amber-500
          done: "#16a34a",        // green-600
          failed: "#dc2626",      // red-600
          unknown: "#6b7280",     // gray-500
        };

        // Prefer consistent ordering in legend + lines
        const order = ["queued", "processing", "done", "failed", "unknown"];

        const chartSeries = ss
          .map((s) => {
            const status = (s.metric.status || "unknown").toLowerCase();
            return {
              name: status,
              points: s.points,
              color: statusColor[status] || statusColor.unknown,
            };
          })
          .sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));

        if (!alive) return;
        setQueueDepthSeries(chartSeries);
      } catch {
        if (!alive) return;
        setQueueDepthSeries([]);
      }

      // Top slugs by request rate (5m)
      try {
        const q = `topk(10, sum by (slug) (rate(ingress_requests_total_by_tenant_total{tenant_id="${tenantUuid}"}[5m])))`;
        //const q = `topk(10, sum by (slug) (rate(ingress_requests_total[5m])))`;
        const resp = await promQuery(q, tenantUuid);
        const ss = toSeries(resp);
        const rows = ss
          .map((s) => ({
            slug: s.metric.slug || "unknown",
            rps: s.points[0]?.v ?? 0,
          }))
          .sort((a, b) => (b.rps ?? 0) - (a.rps ?? 0));
        if (!alive) return;
        setTopSlugs(rows);
      } catch {
        if (!alive) return;
        setTopSlugs([]);
      }
    }

    run();
    const t = window.setInterval(run, 15_000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, [tenantUuid]);

  const hbAge = useMemo(() => ageSec(workerHeartbeat), [workerHeartbeat]);

  return (
    <div className="p-6 space-y-6">
      <div className="text-xl font-semibold">Observability · Platform Health</div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard title="API health" value={apiOk == null ? "…" : apiOk ? "OK" : "DOWN"} />
        <StatCard title="DB connectivity" value={dbOk == null ? "…" : dbOk ? "OK" : "DOWN"} />
        <StatCard
          title="Ingress worker"
          value={workerUp == null ? "…" : workerUp >= 1 ? "UP" : "DOWN"}
          sub={hbAge == null ? "heartbeat: —" : `heartbeat age: ${hbAge}s`}
        />
        <StatCard
          title="Queue depth (queued)"
          value={
            queueDepthSeries.find((s) => s.name === "queued")?.points?.slice(-1)[0]?.v?.toFixed?.(0) ??
            "—"
          }
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <TimeSeriesChart title="Ingress Queue Depth (by status)" series={queueDepthSeries} height={320} />
        <TopKTable
          title="Top slugs (RPS, last 5m)"
          columns={[
            { key: "slug", label: "slug", mono: true },
            { key: "rps", label: "rps" },
          ]}
          rows={topSlugs.map((r) => ({ ...r, rps: (r.rps ?? 0).toFixed(4) }))}
        />
      </div>

      <div className="text-xs text-gray-500">
        Data source: Prometheus via <code>/v1/admin/observability/prom/query(_range)</code>.
      </div>
    </div>
  );
}